'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';


interface PricingPlan {
  id: number
  name: string
  price: string
  description: string
  features: string[]
  isPopular?: boolean
}

interface PricingSectionProps {
  pricingPlans: PricingPlan[]
  loading: boolean
}

export default function PricingSection({ pricingPlans, loading }: PricingSectionProps) {
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<Record<string, string>>({});
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  const createCheckoutSessionWithTimeout = async (planId: string, attempt: number = 1): Promise<{ success: boolean; sessionUrl?: string; error?: string }> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId }),
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const checkoutData = await response.json();

      if (response.ok && checkoutData.sessionUrl) {
        return { success: true, sessionUrl: checkoutData.sessionUrl };
      } else {
        return { success: false, error: checkoutData.error || 'Failed to create checkout session' };
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      
      if (err.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please check your connection and try again.' };
      }
      
      if (!navigator.onLine) {
        return { success: false, error: 'No internet connection. Please check your network and try again.' };
      }
      
      return { success: false, error: 'Network error occurred. Please try again.' };
    }
  };

  const handleGetStarted = async (planName: string) => {
    try {
      // Map plan names to plan IDs
      const planIdMap: Record<string, string> = {
        'Sirloin Cut': 'sirloin',
        'Ribeye Cut': 'ribeye',
        'Tomahawk Cut': 'tomahawk'
      };
      const planId = planIdMap[planName];

      if (!planId) {
        console.error('Invalid plan name:', planName);
        setCheckoutError({ ...checkoutError, [planName]: 'Invalid plan selected. Please try again.' });
        return;
      }

      console.log('handleGetStarted called:', { planName, planId, isAuthenticated, hasUser: !!user });

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        // User is NOT authenticated - redirect to login with plan parameter
        console.log('User not authenticated, redirecting to login with plan:', planName);
        const loginUrl = `/login?plan=${encodeURIComponent(planName)}`;
        console.log('Redirecting to:', loginUrl);
        
        // Use window.location for more reliable redirect
        window.location.href = loginUrl;
        return;
      }

      // User IS authenticated - create checkout session directly with retry logic
      console.log('User authenticated, creating checkout session');
      setCheckoutLoading(planName);
      setCheckoutError({ ...checkoutError, [planName]: '' });
      setRetryCount(0);
      
      let attempt = 1;
      
      while (attempt <= MAX_RETRIES) {
        const result = await createCheckoutSessionWithTimeout(planId, attempt);
        
        if (result.success && result.sessionUrl) {
          // Redirect to Stripe checkout
          console.log('Checkout session created, redirecting to:', result.sessionUrl);
          window.location.href = result.sessionUrl;
          return;
        }
        
        // If failed and we have retries left
        if (attempt < MAX_RETRIES) {
          setRetryCount(attempt);
          setCheckoutError({ ...checkoutError, [planName]: `Checkout failed (attempt ${attempt}/${MAX_RETRIES}). Retrying...` });
          await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5s before retry
          attempt++;
        } else {
          // All retries exhausted
          console.error('Checkout session creation failed after retries:', result.error);
          setCheckoutError({ ...checkoutError, [planName]: result.error || 'Failed to create checkout session after multiple attempts. Please try again later.' });
          setCheckoutLoading(null);
          
          // Show error toast/message for 5 seconds
          setTimeout(() => {
            setCheckoutError(prev => {
              const newErrors = { ...prev };
              delete newErrors[planName];
              return newErrors;
            });
          }, 5000);
          return;
        }
      }
    } catch (error) {
      console.error('Error in handleGetStarted:', error);
      setCheckoutError({ ...checkoutError, [planName]: 'An unexpected error occurred. Please try again.' });
      setCheckoutLoading(null);
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setCheckoutError(prev => {
          const newErrors = { ...prev };
          delete newErrors[planName];
          return newErrors;
        });
      }, 5000);
    }
  };

  return (
    <section id="pricing" className="w-full bg-[#f5f3f7] py-12 sm:py-16 lg:py-24">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 xl:px-36">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14 lg:mb-20">
          <h2 className="text-[36px] sm:text-[56px] lg:text-[80px] font-bebas font-normal leading-[1.1] text-center text-[#3f1651] mb-4 sm:mb-6">
            Choose Your Cut
          </h2>
          <p className="text-[16px] sm:text-[18px] lg:text-[20px] font-poppins font-normal leading-[1.5] text-center text-[#666666] max-w-3xl mx-auto">
            All plans include unlimited requests, unlimited revisions, and pause or cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-[500px] bg-white animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {pricingPlans?.map((plan) => (
              <div
                key={plan?.id}
                className={`relative bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  plan?.isPopular ? 'ring-4 ring-[#8923b3] transform scale-105' : ''
                }`}
              >
                {/* Popular Badge */}
                {plan?.isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#8923b3] text-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6 sm:mb-8 pt-4">
                  <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-bebas font-normal text-[#3f1651] uppercase mb-2">
                    {plan?.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-[40px] sm:text-[48px] lg:text-[56px] font-bebas font-normal text-[#8923b3]">
                      {plan?.price}
                    </span>
                    <span className="text-[16px] sm:text-[18px] font-poppins font-normal text-[#666666]">/month</span>
                  </div>
                  <p className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal text-[#666666] leading-[1.5]">
                    {plan?.description}
                  </p>
                </div>

                {/* Features List */}
                <div className="mb-6 sm:mb-8 space-y-3 sm:space-y-4">
                  {plan?.features?.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#8923b3] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal text-[#666666] leading-[1.6]">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleGetStarted(plan?.name)}
                  disabled={!!checkoutLoading}
                  className={`w-full py-3 sm:py-4 px-6 rounded-lg font-poppins font-semibold text-[14px] sm:text-[15px] lg:text-[16px] transition-all duration-300 ${
                    plan?.isPopular
                      ? 'bg-[#8923b3] text-white hover:bg-[#6f1c8f] shadow-lg hover:shadow-xl'
                      : 'bg-[#f5f3f7] text-[#8923b3] hover:bg-[#8923b3] hover:text-white border-2 border-[#8923b3]'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {checkoutLoading === plan?.name ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Get Started'
                  )}
                </button>
                
                {/* Error Message */}
                {checkoutError[plan?.name] && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600 text-center">{checkoutError[plan?.name]}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Bottom Note */}
        <div className="text-center mt-10 sm:mt-12 lg:mt-16">
          <p className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal text-[#666666] leading-[1.6] max-w-2xl mx-auto">
            Need something custom? <a href="https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call" target="_blank" rel="noopener noreferrer" className="text-[#8923b3] hover:underline font-medium">Talk to us</a> about building a plan that fits your team.
          </p>
        </div>
      </div>
    </section>
  );
}
